import React, { useEffect, useMemo, useState } from 'react';
import { Appointment, Service, Venue, AddOn, PricingTier, Payment } from '@/types';
import {
  Download,
  Filter,
  PoundSterling,
  CreditCard,
  FileText,
  RefreshCcw,
  Printer,
  Search,
  ArrowDown,
  ArrowUp,
  X,
  Loader2
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { Select } from '@shared/components/ui/Select';
import { Badge } from '@shared/components/ui/Badge';
import { StatusBadge } from '@shared/components/common/StatusBadge';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { Modal } from '@shared/components/ui/Modal';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@shared/components/ui/Table';
import { toMoneyNumber } from '@/lib/formatters';
import { formatUkDate, formatUkDateTime } from '@/lib/dates';
import { Pagination } from '@shared/components/common/Pagination';
import { api } from '@/services/api';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { buildMapById } from '@/lib/collections';

interface FinanceTabProps {
  services: Service[];
  addOns: AddOn[];
  venues: Venue[];
}

export const FinanceTab: React.FC<FinanceTabProps> = ({ services, addOns, venues }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalAppointment, setPaymentModalAppointment] = useState<Appointment | null>(null);
  const [paymentModalRecord, setPaymentModalRecord] = useState<Payment | null>(null);
  const [paymentModalLoading, setPaymentModalLoading] = useState(false);
  const [paymentModalError, setPaymentModalError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [dateSortDir, setDateSortDir] = useState<'asc' | 'desc'>('desc');
  const servicesById = useMemo(() => buildMapById(services), [services]);
  const addOnsById = useMemo(() => buildMapById(addOns), [addOns]);
  const venuesById = useMemo(() => buildMapById(venues), [venues]);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [venueFilter, setVenueFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [poSearch, setPoSearch] = useState('');

  const buildBookingParams = ({
    page: pageOverride,
    pageSize: pageSizeOverride
  }: {
    page?: number;
    pageSize?: number;
  } = {}) => ({
    page: pageOverride ?? page,
    pageSize: pageSizeOverride ?? pageSize,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    venueId: venueFilter === 'all' ? undefined : venueFilter,
    status:
      statusFilter === 'all'
        ? undefined
        : statusFilter === 'billable'
          ? 'completed,noshow'
          : statusFilter,
    paymentMethod: paymentFilter === 'all' ? undefined : paymentFilter,
    paymentStatus: paymentStatusFilter === 'all' ? undefined : paymentStatusFilter,
    poNumber: poSearch || undefined,
    q: search || undefined,
    sortBy: 'date',
    sortDir: dateSortDir
  });

  useEffect(() => {
    setPage(1);
  }, [
    dateFrom,
    dateTo,
    venueFilter,
    statusFilter,
    paymentFilter,
    paymentStatusFilter,
    search,
    poSearch,
    dateSortDir
  ]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setLoadError('');
    api
      .listStaffBookings(
        buildBookingParams({
          page,
          pageSize
        })
      )
      .then((data) => {
        if (!isActive) return;
        setAppointments(data.items || []);
        setTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setAppointments([]);
        setTotalCount(0);
        setLoadError(normalizeErrorMessage(err, 'Failed to load finance data.'));
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    page,
    pageSize,
    dateFrom,
    dateTo,
    venueFilter,
    statusFilter,
    paymentFilter,
    paymentStatusFilter,
    search,
    poSearch,
    dateSortDir
  ]);

  const getReminderMeta = (status?: Appointment['reminderStatus']) => {
    const normalized = status || 'pending';

    if (normalized === 'sent') {
      return { label: 'Sent', variant: 'success' as const };
    }

    if (normalized === 'failed') {
      return { label: 'Failed', variant: 'danger' as const };
    }

    if (normalized === 'skipped') {
      return { label: 'Skipped', variant: 'neutral' as const };
    }

    return { label: 'Pending', variant: 'neutral' as const };
  };

  const formatDate = (value?: string | Date | null) => {
    return formatUkDate(value);
  };

  const formatDateTime = (value?: string | Date | null) => {
    return formatUkDateTime(value);
  };

  const formatAmount = (amount?: number | string | null, currency?: string | null) => {
    const normalized = toMoneyNumber(amount || 0);
    const currencyCode = String(currency || '')
      .trim()
      .toUpperCase();
    const prefix = currencyCode && currencyCode !== 'GBP' ? `${currencyCode} ` : '£';
    return `${prefix}${normalized.toFixed(2)}`;
  };

  const formatPaymentStatusLabel = (status?: string) => {
    const normalized = String(status || '').trim();
    if (!normalized) return 'Unknown';
    return normalized
      .split('_')
      .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : ''))
      .join(' ')
      .trim();
  };

  const getPaymentStatusMeta = (method: Appointment['paymentMethod'], status?: string) => {
    const normalized = String(status || '')
      .trim()
      .toLowerCase();

    if (method === 'invoice') {
      return { label: 'Invoiced', variant: 'info' as const };
    }

    if (!normalized) {
      return { label: 'Not Recorded', variant: 'warning' as const };
    }

    if (normalized === 'captured') return { label: 'Captured', variant: 'success' as const };
    if (normalized === 'authorized') return { label: 'Authorized', variant: 'info' as const };
    if (normalized === 'pending_3ds') return { label: 'Pending 3DS', variant: 'warning' as const };
    if (normalized === 'failed') return { label: 'Failed', variant: 'danger' as const };

    return { label: formatPaymentStatusLabel(normalized), variant: 'neutral' as const };
  };

  const getPaymentRef = (app: Appointment) =>
    app.paymentTransactionId || app.paymentVendorTxCode || app.paymentId || '';

  const formatPaymentRef = (app: Appointment) => {
    const raw = getPaymentRef(app);
    if (!raw) return '';
    const value = String(raw);
    if (value.length <= 14) return value;
    return `${value.slice(0, 6)}...${value.slice(-4)}`;
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentModalAppointment(null);
    setPaymentModalRecord(null);
    setPaymentModalError('');
    setPaymentModalLoading(false);
  };

  const openPaymentModal = async (appointment: Appointment) => {
    setPaymentModalAppointment(appointment);
    setPaymentModalOpen(true);
    setPaymentModalRecord(null);
    setPaymentModalError('');
    setPaymentModalLoading(true);

    try {
      const data = await api.listStaffPayments({
        page: 1,
        pageSize: 1,
        appointmentId: appointment.id
      });
      const record = data.items?.[0] || null;
      setPaymentModalRecord(record);
      if (!record && appointment.paymentMethod === 'card') {
        setPaymentModalError('No payment record found for this booking.');
      }
    } catch (err: unknown) {
      setPaymentModalError(normalizeErrorMessage(err, 'Failed to load payment details.'));
    } finally {
      setPaymentModalLoading(false);
    }
  };

  const calculateBookingCost = (app: Appointment) => {
    if (app.totalAmount != null) {
      return toMoneyNumber(app.totalAmount);
    }

    let tier: PricingTier = 'bronze';
    // Use default tier when company pricing is unavailable.

    const service = servicesById.get(app.serviceId);
    let total = 0;

    if (service) {
      const tierPrice = service.tierPrices?.[tier];
      total += toMoneyNumber(tierPrice ?? service.price);
    }

    if (app.addOnIds && app.addOnIds.length > 0) {
      app.addOnIds.forEach((id) => {
        const addon = addOnsById.get(id);
        if (addon) {
          const tierPrice = addon.tierPrices?.[tier];
          total += toMoneyNumber(tierPrice ?? addon.price);
        }
      });
    }

    return total;
  };

  const toggleDateSort = () => {
    setDateSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const renderDateSortIcon = () =>
    dateSortDir === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-slate-600" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-slate-600" />
    );

  const displayAppointments =
    statusFilter === 'billable'
      ? appointments.filter((app) => app.status === 'completed' || app.status === 'noshow')
      : appointments;

  // Aggregations
  const totalRevenue = displayAppointments.reduce((sum, app) => sum + calculateBookingCost(app), 0);
  const totalInvoiced = displayAppointments
    .filter((a) => a.paymentMethod === 'invoice')
    .reduce((sum, app) => sum + calculateBookingCost(app), 0);
  const totalCard = displayAppointments
    .filter((a) => a.paymentMethod === 'card')
    .reduce((sum, app) => sum + calculateBookingCost(app), 0);

  // Print / PDF Handler
  const handlePrint = async () => {
    const printWindow = window.open('', '', 'height=800,width=1000');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Finance Report - ${formatDate(new Date())}</title>
        </head>
        <body>
          <p>Preparing report...</p>
        </body>
      </html>
    `);
    printWindow.document.close();

    try {
      const allAppointments = await fetchAllBookingsForExport();
      const exportAppointments =
        statusFilter === 'billable'
          ? allAppointments.filter((app) => app.status === 'completed' || app.status === 'noshow')
          : allAppointments;

      const totalRevenueForPrint = exportAppointments.reduce(
        (sum, app) => sum + calculateBookingCost(app),
        0
      );
      const totalInvoicedForPrint = exportAppointments
        .filter((a) => a.paymentMethod === 'invoice')
        .reduce((sum, app) => sum + calculateBookingCost(app), 0);
      const totalCardForPrint = exportAppointments
        .filter((a) => a.paymentMethod === 'card')
        .reduce((sum, app) => sum + calculateBookingCost(app), 0);

      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Finance Report - ${formatDate(new Date())}</title>
            <style>
              body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 20px; }
              h1 { font-size: 24px; margin-bottom: 10px; }
              .meta { font-size: 12px; color: #666; margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
              .summary { display: flex; gap: 20px; margin-bottom: 20px; }
              .summary-box { border: 1px solid #ddd; padding: 10px; border-radius: 4px; min-width: 150px; }
              .summary-label { font-size: 10px; color: #666; text-transform: uppercase; font-weight: bold; }
              .summary-value { font-size: 18px; font-weight: bold; }
              table { width: 100%; border-collapse: collapse; font-size: 12px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f8f9fa; font-weight: 600; }
              tr:nth-child(even) { background-color: #f9f9f9; }
              .text-right { text-align: right; }
              @media print {
                .no-print { display: none; }
                body { padding: 0; }
              }
            </style>
          </head>
          <body>
            <h1>Finance Report</h1>
            <div class="meta">
              Generated on: ${formatDateTime(new Date())}<br/>
              Filters Applied: ${venueFilter !== 'all' ? 'Venue Filter' : 'All Venues'}, ${statusFilter !== 'all' ? statusFilter : 'All Statuses'}
            </div>

            <div class="summary">
              <div class="summary-box">
                <div class="summary-label">Total Revenue</div>
                <div class="summary-value">£${totalRevenueForPrint.toFixed(2)}</div>
              </div>
              <div class="summary-box">
                <div class="summary-label">Invoiced</div>
                <div class="summary-value">£${totalInvoicedForPrint.toFixed(2)}</div>
              </div>
              <div class="summary-box">
                <div class="summary-label">Card Payments</div>
                <div class="summary-value">£${totalCardForPrint.toFixed(2)}</div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Ref</th>
                  <th>PO Number</th>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Booked By Name</th>
                  <th>Booked By Email</th>
                  <th>Company</th>
                  <th>Service</th>
                  <th>Add-ons</th>
                  <th>Venue</th>
                  <th>Status</th>
                  <th>Reminder</th>
                  <th>Method</th>
                  <th>Payment Status</th>
                  <th>Payment Ref</th>
                  <th>Payment Provider</th>
                  <th>Captured At</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${exportAppointments
                  .map((app) => {
                    const service = servicesById.get(app.serviceId);
                    const venue = venuesById.get(app.venueId);
                    const addonNames = (app.addOnIds || [])
                      .map((id) => addOnsById.get(id)?.name)
                      .filter(Boolean)
                      .join(', ');
                    const cost = calculateBookingCost(app);
                    const reminderLabel = getReminderMeta(app.reminderStatus).label;
                    const paymentStatusLabel = getPaymentStatusMeta(
                      app.paymentMethod,
                      app.paymentStatus
                    ).label;
                    const paymentRef = getPaymentRef(app);
                    const paymentProvider =
                      app.paymentProvider || (app.paymentMethod === 'invoice' ? 'invoice' : '');
                    const paymentCapturedAt = app.paymentCapturedAt
                      ? formatDateTime(app.paymentCapturedAt)
                      : '';

                    return `
                    <tr>
                      <td>${formatDate(app.date)}</td>
                      <td>${app.time || '-'}</td>
                      <td>${app.id}</td>
                      <td>${app.poNumber || '-'}</td>
                      <td>${app.customerName}</td>
                      <td>${app.customerEmail || '-'}</td>
                      <td>${app.bookedByName || '-'}</td>
                      <td>${app.bookedByEmail || '-'}</td>
                      <td>${app.companyName || '-'}</td>
                      <td>${service ? service.name : 'Unknown'}</td>
                      <td>${addonNames || '-'}</td>
                      <td>${venue ? venue.name : 'Unknown'}</td>
                      <td>${app.status}</td>
                      <td>${reminderLabel}</td>
                      <td>${app.paymentMethod}</td>
                      <td>${paymentStatusLabel}</td>
                      <td>${paymentRef || '-'}</td>
                      <td>${paymentProvider || '-'}</td>
                      <td>${paymentCapturedAt || '-'}</td>
                      <td class="text-right">£${cost.toFixed(2)}</td>
                    </tr>
                  `;
                  })
                  .join('')}
              </tbody>
            </table>
            <script>
              window.onload = function() { window.print(); }
            </script>
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to generate finance report.'));
      try {
        printWindow.close();
      } catch (_closeErr) {
        // ignore
      }
    }
  };

  // Export to CSV
  const fetchAllBookingsForExport = async () => {
    const pageSizeForExport = 100;
    let currentPage = 1;
    let totalCountForExport = 0;
    let items: Appointment[] = [];

    while (true) {
      const data = await api.listStaffBookings(
        buildBookingParams({ page: currentPage, pageSize: pageSizeForExport })
      );
      const pageItems = data.items || [];
      items = items.concat(pageItems);
      totalCountForExport = Number(data.totalCount || 0);

      if (pageItems.length === 0 || items.length >= totalCountForExport) {
        break;
      }
      currentPage += 1;
    }

    return items;
  };

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      const allAppointments = await fetchAllBookingsForExport();
      const exportAppointments =
        statusFilter === 'billable'
          ? allAppointments.filter((app) => app.status === 'completed' || app.status === 'noshow')
          : allAppointments;

      const csvEscape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

      const headers = [
        'Date',
        'Time',
        'Reference',
        'PO Number',
        'Customer',
        'Customer Email',
        'Booked By Name',
        'Booked By Email',
        'Company',
        'Service',
        'Add-ons',
        'Venue',
        'Status',
        'Reminder Status',
        'Payment Method',
        'Payment Status',
        'Payment Reference',
        'Payment Provider',
        'Payment Captured At',
        'Amount (GBP)'
      ];

      const rows = exportAppointments.map((app) => {
        const service = servicesById.get(app.serviceId);
        const venue = venuesById.get(app.venueId);
        const addonNames = app.addOnIds
          .map((id) => addOnsById.get(id)?.name)
          .filter(Boolean)
          .join(', ');

        const paymentStatusLabel = getPaymentStatusMeta(app.paymentMethod, app.paymentStatus).label;
        const paymentRef = getPaymentRef(app);
        const paymentProvider =
          app.paymentProvider || (app.paymentMethod === 'invoice' ? 'invoice' : '');
        const paymentCapturedAt = app.paymentCapturedAt || '';

        return [
          formatDate(app.date),
          app.time,
          app.id,
          app.poNumber || '',
          app.customerName || '',
          app.customerEmail || '',
          app.bookedByName || '',
          app.bookedByEmail || '',
          app.companyName || 'Individual',
          service ? service.name : 'Unknown',
          addonNames,
          venue ? venue.name : 'Unknown',
          app.status,
          getReminderMeta(app.reminderStatus).label,
          app.paymentMethod,
          paymentStatusLabel,
          paymentRef,
          paymentProvider,
          paymentCapturedAt ? formatDateTime(paymentCapturedAt) : '',
          calculateBookingCost(app).toFixed(2)
        ]
          .map((field) => csvEscape(field))
          .join(','); // Quote fields to handle commas
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const csvWithBom = `\ufeff${csvContent}`;
      const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `finance_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to export finance report.'));
    } finally {
      setIsExporting(false);
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setVenueFilter('all');
    setStatusFilter('all');
    setPaymentFilter('all');
    setPaymentStatusFilter('all');
    setSearch('');
    setPoSearch('');
  };

  return (
    <div className="animate-fade-in space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">Total Value</p>
            <h3 className="text-2xl font-bold text-slate-900 mt-1">£{totalRevenue.toFixed(2)}</h3>
            <p className="text-xs text-slate-400 mt-1">{displayAppointments.length} bookings</p>
          </div>
          <div className="bg-emerald-50 p-3 rounded-full text-emerald-600">
            <PoundSterling className="w-6 h-6" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">On Account (Invoiced)</p>
            <h3 className="text-2xl font-bold text-slate-900 mt-1">£{totalInvoiced.toFixed(2)}</h3>
          </div>
          <div className="bg-blue-50 p-3 rounded-full text-blue-600">
            <FileText className="w-6 h-6" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">Paid via Card</p>
            <h3 className="text-2xl font-bold text-slate-900 mt-1">£{totalCard.toFixed(2)}</h3>
          </div>
          <div className="bg-purple-50 p-3 rounded-full text-purple-600">
            <CreditCard className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <Filter className="w-4 h-4 text-teal-600" /> Filter Report
          </h3>
          <div className="flex gap-2">
            <Button
              onClick={clearFilters}
              variant="ghost"
              size="sm"
              className="px-0 text-slate-500 hover:text-red-600"
            >
              <RefreshCcw className="w-3 h-3" /> Reset
            </Button>
            <Button onClick={handlePrint} variant="outline" size="sm">
              <Printer className="w-4 h-4" /> Print / PDF
            </Button>
            <Button onClick={handleExportCSV} variant="dark" size="sm" disabled={isExporting}>
              <Download className="w-4 h-4" /> {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Date From</label>
            <Input
              type="date"
              className="focus:ring-1"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Date To</label>
            <Input
              type="date"
              className="focus:ring-1"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Location</label>
            <Select
              className="focus:ring-1"
              value={venueFilter}
              onChange={(e) => setVenueFilter(e.target.value)}
            >
              <option value="all">All Locations</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Status</label>
            <Select
              className="focus:ring-1"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="billable">Billable Only (Completed/NoShow)</option>
              <option value="completed">Completed</option>
              <option value="noshow">No Show</option>
              <option value="booked">Booked (Forecast)</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Payment Status</label>
            <Select
              className="focus:ring-1"
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
            >
              <option value="all">All Payment Statuses</option>
              <option value="captured">Captured</option>
              <option value="authorized">Authorized</option>
              <option value="pending_3ds">Pending 3DS</option>
              <option value="failed">Failed</option>
              <option value="none">Not Recorded</option>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">General Search</label>
            <Input
              placeholder="Ref, Name, or Email..."
              className="focus:ring-1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">PO Number</label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Search PO..."
                className="pl-7 focus:ring-1"
                value={poSearch}
                onChange={(e) => setPoSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="w-full text-left text-sm">
            <THead className="bg-slate-50 border-b text-slate-600">
              <TR>
                <TH>
                  <button
                    type="button"
                    onClick={toggleDateSort}
                    className="inline-flex items-center gap-1.5 hover:text-slate-800"
                    title="Toggle date sort"
                  >
                    Date
                    {renderDateSortIcon()}
                  </button>
                </TH>
                <TH>Time</TH>
                <TH>Reference</TH>
                <TH>PO Number</TH>
                <TH>Customer / Company</TH>
                <TH>Email</TH>
                <TH>Booked By</TH>
                <TH>Service</TH>
                <TH>Add-ons</TH>
                <TH>Location</TH>
                <TH>Status</TH>
                <TH>Reminder</TH>
                <TH className="min-w-[260px]">Payment</TH>
                <TH align="right">Amount</TH>
              </TR>
            </THead>
            <TBody className="divide-y divide-slate-100">
              {isLoading ? (
                <EmptyRow colSpan={14} className="p-8 text-center text-slate-500">
                  Loading finance data...
                </EmptyRow>
              ) : loadError ? (
                <EmptyRow colSpan={14} className="p-8 text-center text-red-500">
                  {loadError}
                </EmptyRow>
              ) : displayAppointments.length === 0 ? (
                <EmptyRow colSpan={14} className="p-8 text-center text-slate-500 italic">
                  No records found matching your filters.
                </EmptyRow>
              ) : (
                displayAppointments.map((app) => {
                  const service = servicesById.get(app.serviceId);
                  const venue = venuesById.get(app.venueId);
                  const addonNames = (app.addOnIds || [])
                    .map((id) => addOnsById.get(id)?.name)
                    .filter(Boolean)
                    .join(', ');
                  const cost = calculateBookingCost(app);
                  const reminderMeta = getReminderMeta(app.reminderStatus);
                  const paymentMeta = getPaymentStatusMeta(app.paymentMethod, app.paymentStatus);
                  const paymentRef = formatPaymentRef(app);
                  const paymentProvider =
                    app.paymentProvider || (app.paymentMethod === 'invoice' ? 'invoice' : '');
                  const paymentCapturedAt = app.paymentCapturedAt
                    ? formatDateTime(app.paymentCapturedAt)
                    : '';

                  return (
                    <TR key={app.id} className="hover:bg-slate-50">
                      <TD className="text-slate-600">{formatDate(app.date)}</TD>
                      <TD className="text-slate-600">{app.time || '-'}</TD>
                      <TD className="font-mono text-xs">{app.id}</TD>
                      <TD className="font-medium text-slate-800">
                        {app.poNumber || (
                          <span className="text-slate-300 italic text-xs">None</span>
                        )}
                      </TD>
                      <TD className="min-w-[260px]">
                        <div className="font-medium text-slate-900">{app.customerName}</div>
                        <div className="text-xs text-slate-500">
                          {app.companyName || 'Individual'}
                        </div>
                      </TD>
                      <TD className="text-slate-600">{app.customerEmail || '-'}</TD>
                      <TD className="text-slate-600">{app.bookedByEmail || '-'}</TD>
                      <TD className="text-slate-600">{service?.name || app.serviceName || '—'}</TD>
                      <TD className="text-xs text-slate-500">{addonNames || 'None'}</TD>
                      <TD className="text-slate-600">{venue?.name || app.venueName || '—'}</TD>
                      <TD>
                        <StatusBadge status={app.status} />
                      </TD>
                      <TD align="center">
                        <Badge variant={reminderMeta.variant}>{reminderMeta.label}</Badge>
                      </TD>
                      <TD>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {app.paymentMethod === 'invoice' ? (
                              <FileText className="w-3 h-3 text-blue-500" />
                            ) : (
                              <CreditCard className="w-3 h-3 text-purple-500" />
                            )}
                            <span className="capitalize">{app.paymentMethod}</span>
                            <Badge variant={paymentMeta.variant}>{paymentMeta.label}</Badge>
                          </div>
                          <div className="text-[10px] text-slate-400">
                            {paymentProvider || '—'}
                            {paymentRef ? ` · ${paymentRef}` : ''}
                          </div>
                          {paymentMeta.label === 'Captured' && paymentCapturedAt ? (
                            <div className="text-[10px] text-slate-400">
                              Captured {paymentCapturedAt}
                            </div>
                          ) : null}
                          {paymentMeta.label === 'Failed' && app.paymentErrorMessage ? (
                            <div
                              className="text-[10px] text-red-500 truncate max-w-[220px]"
                              title={app.paymentErrorMessage}
                            >
                              {app.paymentErrorMessage}
                            </div>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => openPaymentModal(app)}
                            className="text-[10px] font-bold text-teal-600 hover:text-teal-800 text-left"
                          >
                            View Payment Details
                          </button>
                        </div>
                      </TD>
                      <TD align="right" className="font-medium text-slate-900">
                        £{cost.toFixed(2)}
                      </TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        </div>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        isLoading={isLoading}
      />

      <Modal open={paymentModalOpen} onClose={closePaymentModal} size="lg">
        <div className="border-b bg-slate-50 p-5 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-teal-600" /> Payment Details
            </h3>
            <p className="text-xs text-slate-500">
              Booking Ref: {paymentModalAppointment?.id || '—'}
            </p>
          </div>
          <button onClick={closePaymentModal} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {paymentModalLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading payment details...
            </div>
          ) : (
            (() => {
              const appointment = paymentModalAppointment;
              if (!appointment) {
                return <p className="text-sm text-slate-500">No payment selected.</p>;
              }

              const record = paymentModalRecord;
              const statusLabel = getPaymentStatusMeta(
                appointment.paymentMethod,
                record?.status || appointment.paymentStatus
              ).label;
              const statusVariant = getPaymentStatusMeta(
                appointment.paymentMethod,
                record?.status || appointment.paymentStatus
              ).variant;
              const currency = record?.currency || appointment.currency || 'GBP';
              const subtotal = record?.subtotalAmount ?? appointment.subtotalAmount ?? null;
              const vatRate = record?.vatRatePercent ?? appointment.vatRatePercent ?? null;
              const vatAmount = record?.vatAmount ?? appointment.vatAmount ?? null;
              const totalAmount = record?.totalAmount ?? appointment.totalAmount ?? null;
              const provider =
                record?.provider || appointment.paymentProvider || appointment.paymentMethod;

              return (
                <div className="space-y-5">
                  {paymentModalError ? (
                    <div className="bg-red-50 border border-red-100 text-red-700 text-xs font-semibold rounded-lg p-3">
                      {paymentModalError}
                    </div>
                  ) : null}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase text-slate-400">
                        Payment Method
                      </p>
                      <p className="text-sm font-semibold text-slate-800 capitalize">
                        {appointment.paymentMethod}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase text-slate-400">
                        Payment Status
                      </p>
                      <Badge variant={statusVariant}>{statusLabel}</Badge>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase text-slate-400">Provider</p>
                      <p className="text-sm text-slate-700">{provider || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase text-slate-400">
                        Payment Reference
                      </p>
                      <p className="text-sm text-slate-700 break-all">
                        {record?.providerTransactionId ||
                          record?.providerVendorTxCode ||
                          appointment.paymentTransactionId ||
                          appointment.paymentVendorTxCode ||
                          appointment.paymentId ||
                          '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase text-slate-400">Captured At</p>
                      <p className="text-sm text-slate-700">
                        {formatDateTime(record?.capturedAt || appointment.paymentCapturedAt) || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase text-slate-400">Payment ID</p>
                      <p className="text-sm text-slate-700 break-all">
                        {record?.id || appointment.paymentId || '—'}
                      </p>
                    </div>
                  </div>

                  <div className="border rounded-xl p-4 bg-slate-50">
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-3">
                      Amount Breakdown
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-[10px] uppercase text-slate-400">Subtotal</p>
                        <p className="text-sm font-semibold text-slate-800">
                          {subtotal != null ? formatAmount(subtotal, currency) : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-slate-400">VAT Rate</p>
                        <p className="text-sm text-slate-700">
                          {vatRate != null ? `${Number(vatRate).toFixed(2)}%` : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-slate-400">VAT Amount</p>
                        <p className="text-sm text-slate-700">
                          {vatAmount != null ? formatAmount(vatAmount, currency) : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-slate-400">Total</p>
                        <p className="text-sm font-semibold text-slate-900">
                          {totalAmount != null ? formatAmount(totalAmount, currency) : '—'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {record?.errorMessage || appointment.paymentErrorMessage ? (
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-700">
                      <span className="font-bold">Error:</span>{' '}
                      {record?.errorMessage || appointment.paymentErrorMessage}
                    </div>
                  ) : null}

                  {record?.metadata ? (
                    <div>
                      <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">
                        Payment Metadata
                      </p>
                      <pre className="text-xs bg-slate-900 text-slate-100 rounded-lg p-3 overflow-auto max-h-64">
                        {JSON.stringify(record.metadata, null, 2)}
                      </pre>
                    </div>
                  ) : null}
                </div>
              );
            })()
          )}
        </div>
      </Modal>
    </div>
  );
};
